import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { IAuthContext, IAuthProps } from "./authentication.interface";
import { clearCookies, getCookies, setCookie } from "../../helpers/utils";
import { IAuthTokens } from "../../interfaces/authentication.interface";
import { IUser } from "../../interfaces/user.interface";
import { COOKIES } from "../../constants";
import { useGetUser } from "../../hooks/useUser";
import { useNavigate } from "react-router-dom";
import { logout } from "../../services/authentication.service";
import { routes } from "../../router/routes";
import Button from "../../../components/Button/Button";
import { TranslationContext } from "../translation/translation.provider";

const authenticationInitValues = {
    isAuthenticated: undefined,
    setIsAuthenticated: Function.prototype(),
    authTokens: undefined,
    setAuthTokens: Function.prototype(),
    user: undefined,
    setUser: Function.prototype(),
    setConfirmLogout: Function.prototype()
};

export const AuthenticationContext = createContext<IAuthContext>(authenticationInitValues);

const AuthenticationProvider = ({ children }: IAuthProps) => {
    const { translate } = useContext(TranslationContext);
    const [user, setUser] = useState<IUser>();
    const [confirmLogout, setConfirmLogout] = useState<boolean>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [authTokens, setAuthTokens] = useState<IAuthTokens>();
    const getUserDetails = useGetUser();
    const logoutDialogRef = useRef<HTMLDialogElement>(null);

    const getUser = useCallback(async (): Promise<void> => {
        const response = await getUserDetails.mutateAsync();

        if (response.success) {
            setUser(response.data as IUser);
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
            clearCookies();
        }
    }, [getUserDetails]);

    const logoutUser = useCallback(async () => {
        await logout();

        setIsAuthenticated(false);
        setConfirmLogout(false);
    }, []);

    useEffect(() => {
        if (confirmLogout) {
            logoutDialogRef.current?.showModal();

            logoutDialogRef.current?.addEventListener("keydown", (e: KeyboardEvent) => {
                if (e.key === "Escape") setConfirmLogout(false);
            });
        } else logoutDialogRef.current?.close();
    }, [confirmLogout]);

    useEffect(() => {
        const cookies = getCookies();

        if (cookies[COOKIES.REFRESH_TOKEN]) {
            setAuthTokens({
                access_token: cookies[COOKIES.TOKEN] as string,
                refresh_token: cookies[COOKIES.REFRESH_TOKEN] as string
            });
        } else {
            setIsAuthenticated(false);
        }
    }, []);

    useEffect(() => {
        const cookies = getCookies();
        if (authTokens) {
            getUser();
            setCookie(
                COOKIES.TOKEN,
                authTokens?.access_token,
                COOKIES.TOKEN_EXP_HOURS,
                "/",
                null,
                true
            );
            setCookie(
                COOKIES.REFRESH_TOKEN,
                authTokens?.refresh_token,
                COOKIES.REFRESH_TOKEN_EXP_HOURS,
                "/",
                null,
                true
            );
        } else if (!cookies[COOKIES.TOKEN] && !cookies[COOKIES.REFRESH_TOKEN]) {
            setIsAuthenticated(false);
        }
    }, [authTokens]);

    return (
        <AuthenticationContext.Provider
            value={{
                isAuthenticated,
                setIsAuthenticated,
                authTokens,
                setAuthTokens,
                user,
                setUser,
                setConfirmLogout
            }}>
            {confirmLogout && (
                <dialog ref={logoutDialogRef} className="modal_logout py-31 px-22">
                    <h2 className="text-h4-md mb-12 text-center">
                        {translate(
                            "authentication.logout_confirm",
                            "Are you sure you want to logout?"
                        )}
                    </h2>
                    <div className="d-flex gap-02">
                        <Button
                            type="button"
                            onClick={logoutUser}
                            variant="solid"
                            customCss="w-100"
                            label={translate("common.confirm", "Confirm")}></Button>
                        <Button
                            type="button"
                            onClick={() => setConfirmLogout(false)}
                            variant="outlined"
                            customCss="w-100"
                            label={translate("common.cancel", "Cancel")}></Button>
                    </div>
                </dialog>
            )}
            {children}
        </AuthenticationContext.Provider>
    );
};

export default AuthenticationProvider;
